import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, ArrowLeft, ArrowRight, BookOpen, Gamepad2, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

type LetterInfo = {
  letter: string;
  word: string;
  emoji: string;
  meaning: string;
};

const ALPHABET: LetterInfo[] = [
  { letter: "A", word: "Apple", emoji: "🍎", meaning: "تفاحة" },
  { letter: "B", word: "Ball", emoji: "⚽", meaning: "كرة" },
  { letter: "C", word: "Cat", emoji: "🐱", meaning: "قطة" },
  { letter: "D", word: "Dog", emoji: "🐶", meaning: "كلب" },
  { letter: "E", word: "Egg", emoji: "🥚", meaning: "بيضة" },
  { letter: "F", word: "Fish", emoji: "🐟", meaning: "سمكة" },
  { letter: "G", word: "Grape", emoji: "🍇", meaning: "عنب" },
  { letter: "H", word: "House", emoji: "🏠", meaning: "بيت" },
  { letter: "I", word: "Ice cream", emoji: "🍦", meaning: "آيس كريم" },
  { letter: "J", word: "Juice", emoji: "🧃", meaning: "عصير" },
  { letter: "K", word: "King", emoji: "🤴", meaning: "ملك" },
  { letter: "L", word: "Lion", emoji: "🦁", meaning: "أسد" },
  { letter: "M", word: "Moon", emoji: "🌙", meaning: "قمر" },
  { letter: "N", word: "Nose", emoji: "👃", meaning: "أنف" },
  { letter: "O", word: "Orange", emoji: "🍊", meaning: "برتقالة" },
  { letter: "P", word: "Pencil", emoji: "✏️", meaning: "قلم رصاص" },
  { letter: "Q", word: "Queen", emoji: "👸", meaning: "ملكة" },
  { letter: "R", word: "Rainbow", emoji: "🌈", meaning: "قوس قزح" },
  { letter: "S", word: "Sun", emoji: "☀️", meaning: "شمس" },
  { letter: "T", word: "Tree", emoji: "🌳", meaning: "شجرة" },
  { letter: "U", word: "Umbrella", emoji: "☂️", meaning: "مظلة" },
  { letter: "V", word: "Violin", emoji: "🎻", meaning: "كمان" },
  { letter: "W", word: "Water", emoji: "💧", meaning: "ماء" },
  { letter: "X", word: "Xylophone", emoji: "🎵", meaning: "إكسيليفون" },
  { letter: "Y", word: "Yellow", emoji: "💛", meaning: "أصفر" },
  { letter: "Z", word: "Zebra", emoji: "🦓", meaning: "حمار وحشي" },
];

type Question = {
  question: string;
  image: string;
  options: string[];
  answer: string;
};

const buildQuizQuestions = (): Question[] => {
  const questions: Question[] = ALPHABET.map((l) => ({
    question: `ما الحرف الذي يبدأ به ${l.word}؟`,
    image: l.emoji,
    options: shuffleArray([
      l.letter,
      ...shuffleArray(ALPHABET.filter((x) => x.letter !== l.letter).map((x) => x.letter)).slice(0, 3),
    ]),
    answer: l.letter,
  }));
  return shuffleArray(questions);
};

type Phase = "learn" | "quiz" | "result";

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function EnglishGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [phase, setPhase] = useState<Phase>("learn");
  const [letterIndex, setLetterIndex] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const currentLetter = ALPHABET[letterIndex];

  const { speak, isSpeaking, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    lang: "en-US",
    rate: 0.9,
    pitch: 1.2,
    onVoiceResult: (transcript) => {
      if (phase !== "quiz" || selected !== null || round >= totalRounds) return;
      const spoken = transcript.trim().toUpperCase();
      const data = questions[round];
      if (!data) return;
      const match = data.options.find(opt => spoken.includes(opt) || spoken.startsWith(opt));
      if (match) handleSelect(match);
    },
  });

  const startQuiz = () => {
    setQuestions(buildQuizQuestions().slice(0, totalRounds));
    setRound(0);
    setScore(0);
    setStreak(0);
    setSelected(null);
    setCompleted(false);
    setPhase("quiz");
  };

  const resetAll = () => {
    setPhase("learn");
    setLetterIndex(0);
    setCompleted(false);
  };

  const handleSelect = (opt: string) => {
    if (selected !== null) return;
    const data = questions[round];
    setSelected(opt);
    if (opt === data.answer) {
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setRound((r) => r + 1);
    }, timeout);
  };

  useEffect(() => {
    if (phase === "quiz" && round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
      setPhase("result");
    }
  }, [round, phase]);

  // ===== LEARN PHASE =====
  if (phase === "learn") {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="text-sm px-3 py-1">📖 تعلّم الحروف</Badge>
          <Badge variant="outline" className="text-sm px-3 py-1">{letterIndex + 1} / {ALPHABET.length}</Badge>
        </div>

        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-600"
            animate={{ width: `${((letterIndex + 1) / ALPHABET.length) * 100}%` }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={letterIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <Card className="shadow-elevated overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-violet-500 to-fuchsia-700 p-8 text-center text-white relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-3 left-3 h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full"
                    onClick={() => speak(`${currentLetter.letter}. ${currentLetter.word}.`)}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <motion.span
                    className="text-9xl font-black block mb-2"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    {currentLetter.letter}
                  </motion.span>
                  <p className="text-2xl font-light opacity-80 lowercase">{currentLetter.letter.toLowerCase()}</p>
                </div>
                <div className="p-6 text-center space-y-3 bg-card">
                  <motion.span
                    className="text-6xl block"
                    animate={{ y: [0, -6, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    {currentLetter.emoji}
                  </motion.span>
                  <p className="text-2xl font-bold text-foreground">{currentLetter.word}</p>
                  <p className="text-lg text-muted-foreground">{currentLetter.meaning}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setLetterIndex((i) => i - 1)}
            disabled={letterIndex === 0}
            className="flex-1"
          >
            <ArrowRight className="w-4 h-4 ml-1" />
            السابق
          </Button>

          {letterIndex < ALPHABET.length - 1 ? (
            <Button
              onClick={() => setLetterIndex((i) => i + 1)}
              className="flex-1 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white border-0"
            >
              التالي
              <ArrowLeft className="w-4 h-4 mr-1" />
            </Button>
          ) : (
            <Button
              onClick={startQuiz}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0"
            >
              <Gamepad2 className="w-4 h-4 ml-1" />
              ابدأ الاختبار!
            </Button>
          )}
        </div>

        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={startQuiz} className="text-muted-foreground">
            تخطي التعلّم وابدأ الاختبار مباشرة ←
          </Button>
        </div>
      </div>
    );
  }

  // ===== RESULT PHASE =====
  if (phase === "result") {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="text-6xl">
              🇬🇧
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">!Excellent 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <Button onClick={resetAll} size="lg" variant="outline" className="flex-1">
                <BookOpen className="w-4 h-4 ml-2" />
                تعلّم مرة أخرى
              </Button>
              <Button onClick={startQuiz} size="lg" className="flex-1 gradient-hero border-0 text-primary-foreground">
                <RotateCcw className="w-4 h-4 ml-2" />
                اختبار جديد
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ===== QUIZ PHASE =====
  const data = questions[round] || questions[0];

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-600" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-violet-500 to-fuchsia-700 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full"
                    onClick={() => speak(data.question)}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={isListening ? "destructive" : "ghost"}
                    size="icon"
                    className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`}
                    onClick={() => isListening ? stopListening() : startListening()}
                    disabled={!!selected}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <motion.span
                  className="text-7xl block mb-4"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  {data.image}
                </motion.span>
                <p className="text-lg font-medium opacity-95">{data.question}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-4 text-center transition-all ${bgClass}`}
            >
              <span className="text-2xl font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
